import ResponsiveImage from "../../components/common/ResponsiveImage";
import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Heart,
  Loader2,
  Star,
  ChevronLeft,
  ChevronRight,
  Binoculars,
  Home as HomeIcon,
  Search,
  User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import { useAuth } from "../../lib/auth";
import { publicShopUrl } from "../../lib/shopUrls";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ---------- helpers ---------- */
const timeAgo = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w}w ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/* Render text and turn `#hashtags` into a styled inline span. */
const renderRichText = (text) => {
  if (!text) return null;
  const parts = text.split(/(\s+)/);
  return parts.map((p, i) => {
    if (/^#\w{2,30}$/.test(p)) {
      return (
        <span
          key={i}
          data-testid="post-hashtag"
          className="font-semibold text-[#0F1115]"
        >
          {p}
        </span>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

/* ---------- product card (inside a post) ---------- */
const ProductPreview = ({ product, onOpen }) => {
  usePartnerTranslation();
  if (!product) return null;
  return (
    <div
      data-testid={`post-product-${product.id}`}
      className="overflow-hidden rounded-xl border border-[#EAECEF] bg-white"
    >
      {product.cover && (
        <button
          type="button"
          onClick={() => onOpen(product.id)}
          className="block w-full"
          aria-label={tr("Open {{value0}}", { value0: product.title })}
        >
          <ResponsiveImage imageWidth={960}
            src={product.cover}
            alt={product.title}
            className="h-48 w-full object-cover transition-transform hover:scale-[1.02] sm:h-56"
            loading="lazy"
          />
        </button>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 sm:p-4">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onOpen(product.id)}
            className="block truncate text-start text-base font-extrabold text-[#0F1115] hover:underline"
          >
            {product.title}
          </button>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#6B7280]">
            {product.duration_label && (
              <span className="inline-flex items-center gap-1">
                ⏱ {product.duration_label}
              </span>
            )}
            {product.category && (
              <span className="inline-flex items-center gap-1">
                👥 {product.category}
              </span>
            )}
          </div>
          {product.from_price ? (
            <p className="mt-1 text-xs text-[#6B7280]">{tr("from")}{" "}
              <span className="text-base font-extrabold text-[#0F1115]">
                {product.from_currency || ""} {product.from_price}
              </span>
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => onOpen(product.id)}
          className="inline-flex h-9 items-center rounded-md bg-[#2476FF] px-4 text-xs font-semibold text-white hover:bg-[#1857c4]"
          data-testid={`post-product-cta-${product.id}`}
        >{tr("See details")}</button>
      </div>
    </div>
  );
};

/* ---------- photo carousel ---------- */
const PhotoCarousel = ({ photos }) => {
  usePartnerTranslation();
  const [idx, setIdx] = useState(0);
  if (!photos || photos.length === 0) return null;
  const single = photos.length === 1;
  return (
    <div
      data-testid="post-photos"
      className="relative overflow-hidden rounded-md bg-black"
    >
      <div
        className="flex transition-transform duration-300"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {photos.map((p, i) => (
          <ResponsiveImage imageWidth={960}
            key={i}
            src={p.url}
            alt={p.name || tr("Photo {{value0}}", { value0: i + 1 })}
            className="h-auto w-full flex-shrink-0 object-cover"
            style={{ aspectRatio: "16/9" }}
            loading="lazy"
          />
        ))}
      </div>
      {!single && (
        <>
          <button
            type="button"
            onClick={() =>
              setIdx((v) => (v - 1 + photos.length) % photos.length)
            }
            aria-label={tr("Previous photo")}
            className="absolute start-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/75"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setIdx((v) => (v + 1) % photos.length)}
            aria-label={tr("Next photo")}
            className="absolute end-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/75"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1">
            {photos.map((_, i) => (
              <span
                key={i}
                className={cn(
                  "h-1.5 rounded-full bg-white/55 transition-all",
                  i === idx ? "w-4 bg-white" : "w-1.5",
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* ---------- single post ---------- */
const PostCard = ({ post }) => {
  usePartnerTranslation();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likeCount, setLikeCount] = useState(post.like_count);

  const goShop = () => {
    if (!post.author_handle) return;
    window.location.href = publicShopUrl(post.author_handle);
  };
  const goProduct = (id) => {
    if (id) navigate(`/products/${id}/edit`);
  };

  const toggleLike = async () => {
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      const res = await axios.post(`${API}/stream/posts/${post.id}/like`);
      if (res.data?.success) {
        setLiked(res.data.liked_by_me);
        setLikeCount(res.data.like_count);
      }
    } catch (err) {
      setLiked(!next);
      setLikeCount((c) => Math.max(0, c + (next ? -1 : 1)));
      toast.error(tr("Couldn't update like"), {
        description: err?.response?.data?.detail || "Please retry.",
      });
    }
  };

  const isReview = post.kind === "review";

  return (
    <article
      data-testid={`post-${post.id}`}
      className="space-y-3 border-b border-[#F0E5E5] py-5 first:pt-0 last:border-b-0"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-1">
        <button
          type="button"
          onClick={goShop}
          className="flex-shrink-0 rounded-full transition-transform hover:scale-105"
          data-testid={`post-author-avatar-${post.id}`}
          aria-label={tr("Open shop {{value0}}", { value0: post.author_name })}
        >
          <ResponsiveImage imageWidth={960}
            src={post.author_avatar}
            alt={post.author_name}
            className="h-9 w-9 rounded-full object-cover"
          />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <button
              type="button"
              onClick={goShop}
              data-testid={`post-author-name-${post.id}`}
              className="truncate text-sm font-extrabold text-[#0F1115] hover:underline"
            >
              {post.author_name}
            </button>
            {post.author_kind === "supplier" && (
              <span className="inline-flex h-4 items-center rounded bg-[#FF5757] px-1.5 text-[10px] font-bold uppercase tracking-wider text-white">{tr("Partner")}</span>
            )}
            <span className="text-xs text-[#9BA1AB]">·</span>
            <span className="text-xs text-[#9BA1AB]">
              {timeAgo(post.created_at)}
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      {post.text && (
        <p className="whitespace-pre-wrap break-words px-1 text-sm leading-relaxed text-[#0F1115]">
          {renderRichText(post.text)}
        </p>
      )}

      {/* Stars for review posts — match the image: red stars below text */}
      {isReview && (
        <div className="flex items-center justify-center gap-1 px-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              className={cn(
                "h-4 w-4",
                n <= (post.rating || 0)
                  ? "fill-[#FF5757] text-[#FF5757]"
                  : "fill-[#E6E8EC] text-[#E6E8EC]",
              )}
              strokeWidth={0}
            />
          ))}
        </div>
      )}

      {/* Photos */}
      {post.photos?.length > 0 && <PhotoCarousel photos={post.photos} />}

      {/* Product card */}
      {post.product && <ProductPreview product={post.product} onOpen={goProduct} />}

      {/* Like row only — no comments */}
      <div className="flex items-center gap-2 px-1 pt-1">
        <button
          type="button"
          onClick={toggleLike}
          data-testid={`post-like-${post.id}`}
          aria-pressed={liked}
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors",
            liked
              ? "text-[#FF5757] hover:bg-[#FFEFEF]"
              : "text-[#6B7280] hover:bg-[#F3EEEE]",
          )}
        >
          <Heart
            className={cn(
              "h-5 w-5",
              liked && "fill-[#FF5757] text-[#FF5757]",
            )}
            strokeWidth={liked ? 0 : 2}
          />
        </button>
        <span
          data-testid={`post-likes-count-${post.id}`}
          className="text-xs font-semibold text-[#6B7280]"
        >
          {likeCount} {likeCount === 1 ? tr("like") : tr("likes")}
        </span>
      </div>
    </article>
  );
};

/* ---------- top header — centred binoculars logo ---------- */
const StreamHeader = () => {
  usePartnerTranslation();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-30 border-b border-[#F0E5E5] bg-[#FFF7F7]/95 backdrop-blur">
      <div className="mx-auto flex h-12 max-w-[600px] items-center justify-center px-4">
        <button
          type="button"
          onClick={() => navigate("/stream")}
          aria-label={tr("Stream")}
          data-testid="stream-logo"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#FF5757] transition-colors hover:bg-[#FFEFEF]"
        >
          <Binoculars className="h-5 w-5" strokeWidth={1.8} />
        </button>
      </div>
    </header>
  );
};

/* ---------- left sticky nav ---------- */
const LeftRail = () => {
  usePartnerTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const items = [
    {
      icon: HomeIcon,
      label: tr("Home"),
      testid: "rail-home",
      onClick: () => navigate("/home"),
    },
    {
      icon: Search,
      label: tr("Search"),
      testid: "rail-search",
      onClick: () => navigate("/products"),
    },
    {
      icon: UserIcon,
      label: tr("Profile"),
      testid: "rail-profile",
      onClick: () => navigate("/my-shop"),
    },
  ];
  return (
    <nav
      data-testid="stream-left-rail"
      aria-label={tr("Stream shortcuts")}
      className="fixed start-3 top-1/2 z-20 hidden -translate-y-1/2 flex-col items-center gap-1 rounded-full border border-[#F0E5E5] bg-white/95 px-1.5 py-2 shadow-sm backdrop-blur md:flex"
    >
      {items.map(({ icon: Icon, label, testid, onClick }, idx) => (
        <React.Fragment key={testid}>
          {idx === 1 && (
            <span aria-hidden className="my-0.5 h-px w-5 bg-[#F0E5E5]" />
          )}
          <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            aria-label={label}
            title={label}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#6B7280] transition-colors hover:bg-[#FFEFEF] hover:text-[#FF5757]"
          >
            <Icon className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </React.Fragment>
      ))}
      {user && (
        <>
          <span aria-hidden className="my-0.5 h-px w-5 bg-[#F0E5E5]" />
          <ResponsiveImage imageWidth={960}
            src={`https://i.pravatar.cc/60?u=${user.email}`}
            alt={user.full_name || user.email}
            className="h-7 w-7 rounded-full object-cover ring-1 ring-[#F0E5E5]"
            data-testid="rail-avatar"
          />
        </>
      )}
    </nav>
  );
};

/* ---------- main page ---------- */
export default function StreamPage() {
  usePartnerTranslation();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(
    async ({ append = false } = {}) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const res = await axios.get(`${API}/stream/feed`, {
          params: { cursor: append ? cursor : undefined, limit: 20 },
        });
        setItems((prev) =>
          append ? [...prev, ...(res.data.items || [])] : res.data.items || [],
        );
        setCursor(res.data.next_cursor || null);
        setHasMore(!!res.data.has_more);
      } catch (err) {
        toast.error(tr("Couldn't load the Stream"), {
          description: err?.response?.data?.detail || "Please retry.",
        });
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [cursor],
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="stream-page"
      className="min-h-screen bg-[#FFF7F7]"
    >
      <StreamHeader />
      <LeftRail />

      <main className="mx-auto w-full max-w-[600px] px-3 pb-16 pt-4 sm:px-4 sm:pt-6">
        {loading && (
          <div className="flex items-center justify-center py-12 text-[#6B7280]">
            <Loader2 className="me-2 h-4 w-4 animate-spin" />{tr("Loading the Stream…")}</div>
        )}
        {!loading && items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#F0E5E5] bg-white py-10 text-center text-sm text-[#9BA1AB]">{tr("Nothing to show yet.")}</div>
        )}
        {items.length > 0 && (
          <div className="rounded-2xl border border-[#F0E5E5] bg-white px-3 sm:px-4">
            {items.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        )}
        {hasMore && (
          <div className="flex justify-center pt-4">
            <button
              type="button"
              onClick={() => load({ append: true })}
              disabled={loadingMore}
              data-testid="stream-load-more"
              className="inline-flex h-10 items-center gap-1.5 rounded-full border border-[#F0E5E5] bg-white px-5 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115] disabled:opacity-60"
            >
              {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}{tr("Load more")}</button>
          </div>
        )}
      </main>
    </div>
  );
}
